import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtQty, downloadReport, errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { PageHeader } from "../components/common";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, FileText, FileSpreadsheet } from "lucide-react";

export default function MntInternalOrder() {
  const { t } = useI18n();
  const { eid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");

  const load = () => {
    const params = {};
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    api.get(`/mnt/equipment/${eid}/internal-order`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  };
  useEffect(load, [eid, df, dt]);

  const exportFile = async (fmt) => {
    try {
      const parts = [];
      if (df) parts.push(`date_from=${df}`);
      if (dt) parts.push(`date_to=${dt}`);
      parts.push(`format=${fmt}`);
      await downloadReport(
        `/reports/mnt/internal-order/${eid}?${parts.join("&")}`,
        `orden_interna_${data?.equipment?.code || eid}.${fmt === "xlsx" ? "xlsx" : fmt}`
      );
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (data === null) return <div className="text-xs text-muted-foreground p-6">Cargando…</div>;
  if (data === false) return <div className="text-xs text-muted-foreground p-6">Not found</div>;

  const eq = data.equipment;
  const totals = data.totals || {};

  return (
    <div data-testid="mnt-internal-order-page">
      <button onClick={() => navigate(-1)} className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground" data-testid="io-back">
        <ArrowLeft size={12} /> Volver
      </button>
      <PageHeader title={t("mnt.io.title")} subtitle={t("mnt.io.subtitle")}>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.h.dateFrom")}</Label>
            <Input type="date" value={df} onChange={(e) => setDf(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="io-date-from" />
          </div>
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.h.dateTo")}</Label>
            <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="io-date-to" />
          </div>
          <div className="flex border">
            <button onClick={() => exportFile("csv")} className="border-r px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="io-export-csv"><FileText size={12} /> CSV</button>
            <button onClick={() => exportFile("xlsx")} className="px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="io-export-xlsx"><FileSpreadsheet size={12} /> XLSX</button>
          </div>
        </div>
      </PageHeader>

      <div className="border bg-card p-4 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.io.number")}</div>
            <div className="text-2xl font-black tabular-nums leading-tight" data-testid="io-number">{data.internal_order_number}</div>
            <div className="text-xs mt-1"><span className="font-bold tabular-nums">{eq.code}</span> · {eq.name}{eq.model ? <span className="text-muted-foreground"> · {eq.model}</span> : null}</div>
            <div className="text-[11px] text-muted-foreground">{eq.area}{eq.location ? ` · ${eq.location}` : ""}</div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right">
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.io.wosCount")}</div>
            <div className="text-lg font-black tabular-nums" data-testid="io-wos-count">{data.work_orders_count}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.laborCost")}</div>
            <div className="tabular-nums font-bold">{fmtMoney(totals.labor_cost || 0)}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.materialCost")}</div>
            <div className="tabular-nums font-bold">{fmtMoney(totals.materials_cost || 0)}</div>
            {totals.direct_purchases_cost > 0 && (
              <>
                <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Compras directas</div>
                <div className="tabular-nums font-bold text-blue-700">{fmtMoney(totals.direct_purchases_cost)}</div>
              </>
            )}
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.totalCost")}</div>
            <div className="text-lg font-black tabular-nums text-primary" data-testid="io-total-cost">{fmtMoney(totals.total_cost || 0)}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("mnt.wo.downtime")}</div>
            <div className="tabular-nums">{fmtQty(totals.downtime_hours || 0)} h</div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-widest mb-2">{t("nav.mnt.workOrders")} ({data.work_orders.length})</div>
        {data.work_orders.length === 0 ? <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">—</div> : (
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr>
              <th>{t("mnt.wo.number")}</th><th>{t("mnt.wo.type")}</th><th>{t("mnt.description")}</th>
              <th>{t("mnt.wo.technician")}</th><th>{t("common.status")}</th><th>{t("mnt.wo.totalCost")}</th><th>{t("mnt.wo.actualEnd")}</th>
            </tr></thead>
            <tbody>
              {data.work_orders.map((w) => (
                <tr key={w.id} onClick={() => navigate(`/mnt/work-orders/${w.id}`)} className="cursor-pointer hover:bg-muted/50" data-testid={`io-wo-${w.wo_number}`}>
                  <td className="font-bold tabular-nums">{w.wo_number}</td>
                  <td className="text-[10px] font-bold">{w.type}</td>
                  <td className="text-[11px] max-w-64 truncate">{w.description}</td>
                  <td className="text-[11px] font-bold">{w.technician_name || w.assigned_to || "—"}</td>
                  <td className="text-[10px] font-bold uppercase">{w.status}</td>
                  <td className="tabular-nums font-bold">{fmtMoney(w.total_cost || 0)}</td>
                  <td className="text-[10px] text-muted-foreground tabular-nums">{w.actual_end ? fmtDate(w.actual_end) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest mb-2">{t("mnt.io.consumption")} ({data.consumption_lines.length})</div>
        {data.consumption_lines.length === 0 ? <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">—</div> : (
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr>
              <th>Fecha</th><th>OT</th><th>Artículo</th><th>Cantidad</th><th>Costo Unit.</th><th>Costo Total</th><th>Almacén</th><th>Usuario</th>
            </tr></thead>
            <tbody>
              {data.consumption_lines.map((c) => (
                <tr key={c.id} data-testid={`io-cx-${c.id}`}>
                  <td className="text-[10px] tabular-nums text-muted-foreground">{fmtDate(c.created_at)}</td>
                  <td className="font-bold tabular-nums">{c.doc_number}</td>
                  <td className="text-[11px]"><span className="font-bold tabular-nums">{c.item_number}</span> · {c.item_description}</td>
                  <td className="tabular-nums font-bold">{fmtQty(c.qty)} {c.uom}</td>
                  <td className="tabular-nums">{fmtMoney(c.unit_cost)}</td>
                  <td className="tabular-nums font-bold">{fmtMoney(c.total_cost)}</td>
                  <td className="text-[10px]">{c.warehouse}</td>
                  <td className="text-[10px] text-muted-foreground">{c.user_email}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest mb-2">Compras directas al equipo ({data.direct_purchases?.length || 0})</div>
        {(!data.direct_purchases || data.direct_purchases.length === 0) ? <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">—</div> : (
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr>
              <th>Requisición</th><th>Categoría</th><th>Estatus</th><th>OT origen</th>
              <th>PO</th><th>Líneas</th><th>Total</th><th>Fecha</th>
            </tr></thead>
            <tbody>
              {data.direct_purchases.map((r) => (
                <tr key={r.id} data-testid={`io-dp-${r.number}`}>
                  <td className="font-bold tabular-nums">{r.number}</td>
                  <td className="text-[10px] font-bold uppercase">{r.category || "—"}</td>
                  <td className="text-[10px] font-bold uppercase">{r.status}</td>
                  <td className="text-[10px] font-bold">{r.wo_number || <span className="text-muted-foreground">directa</span>}</td>
                  <td className="text-[11px] font-bold">{r.po_number || "—"}</td>
                  <td className="text-[10px]">{r.lines.length}</td>
                  <td className="tabular-nums font-bold">{fmtMoney(r.total)}</td>
                  <td className="text-[10px] text-muted-foreground tabular-nums">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
